/**
 * Pure helpers for the meter readings card (spec 2026-10-27-18, part B.5):
 * the monthly consumption chart's series.
 */

export interface ConsumptionPeriodLike {
  period_start: string
  consumption?: number | null
  quality: string
  heating_degree_days?: number | null
  cooling_degree_days?: number | null
}

export interface ChartPoint {
  month: string // YYYY-MM
  value: number | null
  lastYear: number | null
  insufficient: boolean
}

/** Shifts a YYYY-MM-DD month start by a number of years, as YYYY-MM. */
function monthKey(periodStart: string, yearShift = 0): string {
  const [y, m] = periodStart.split('-')
  return `${Number(y) + yearShift}-${m}`
}

/**
 * The chart's points: each month's consumption (per degree day when
 * `normalized`, cooling degree days for a cooling meter), the same month a
 * year earlier as the comparison line, and insufficient months flagged so the
 * chart hatches them. A month whose degree days are missing has no normalized
 * value.
 */
export function chartPoints(periods: ConsumptionPeriodLike[], normalized: boolean, cooling = false): ChartPoint[] {
  const valueOf = (p: ConsumptionPeriodLike): number | null => {
    if (p.quality === 'insufficient' || p.consumption == null) return null
    if (!normalized) return p.consumption
    const dd = cooling ? p.cooling_degree_days : p.heating_degree_days
    if (!dd || dd <= 0) return null
    return Math.round((p.consumption / dd) * 1000) / 1000
  }
  const byMonth = new Map(periods.map((p) => [monthKey(p.period_start), p]))
  return periods.map((p) => {
    const prior = byMonth.get(monthKey(p.period_start, -1))
    return {
      month: monthKey(p.period_start),
      value: valueOf(p),
      lastYear: prior ? valueOf(prior) : null,
      insufficient: p.quality === 'insufficient',
    }
  })
}

/** Readings sorted the way the card lists them: newest first. */
export function sortReadings<T extends { read_at: string; created_at?: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => b.read_at.localeCompare(a.read_at) || (b.created_at ?? '').localeCompare(a.created_at ?? ''))
}
